/* Original name @uswitch/trustyle.flex-grid */
/* eslint-disable no-magic-numbers */
/** @jsx jsx */

import * as React from 'react'
import classNames from 'classnames'
import { jsx, useThemeUI } from 'theme-ui'

const getGutterSize = (theme) => theme?.layout && theme?.layout['flex-grid'].sizes?.gutter

const getVerticalGutterSize = (theme) =>
  theme?.layout && theme?.layout['flex-grid'].sizes?.verticalGutter

const getContainerSize = (theme) =>
  (theme?.layout && theme?.layout['flex-grid']?.container?.maxWidth) || 1200

export const useContainerSize = () => {
  const { theme } = useThemeUI()
  return getContainerSize(theme)
}

const castArray = (valueOrArray) => [].concat(valueOrArray) // wiiilllllson

const getValueFromSize = (theme, size) => (typeof size === 'number' ? size : theme.space[size] || 0)

const getHalf = (value) => value / 2

// if we originally had a value outside of an array return it that way
// otherwise the undefined values will cause incorrect responsivness
const uncastArrayIfSingle = (arr) => (arr.length === 1 ? arr[0] : arr)

const toPx = (value) => `${value}px`

const getValueFromBreakpointIndex = (arr, index) => {
  if (arr[index] !== undefined) return arr[index]
  return arr[arr.length - 1]
}

export const isChildOfContainerContext = React.createContext()
export const columnSizeContext = React.createContext()

export const Container = ({ children, cols, span, className = '', ...props }) => {
  const classes = classNames('flex-grid-container', className)
  const colsArray = castArray(cols)
  const spanArray = castArray(span)
  return (
    <div
      sx={{
        mx: 'auto',
        boxSizing: 'border-box',
        px: getGutterSize,
        maxWidth: (theme) =>
          uncastArrayIfSingle(
            castArray(getContainerSize(theme)).map(
              (maxWidth, i) =>
                maxWidth * (colsArray[i] && spanArray[i] ? spanArray[i] / colsArray[i] : 1),
            ),
          ),
        variant: 'compounds.container.flex',
      }}
      {...props}
      className={classes}
    >
      <isChildOfContainerContext.Provider value={true}>
        {children}
      </isChildOfContainerContext.Provider>
    </div>
  )
}

export const Row = ({
  children,
  cols = 12,
  direction = ['column', 'row', 'row'],
  wrap = true,
  ...props
}) => {
  const childrenArray = Array.isArray(children) ? children : [children]
  return (
    <div
      sx={{
        mx: (theme) =>
          uncastArrayIfSingle(
            castArray(getGutterSize(theme))
              .map((value) => getValueFromSize(theme, value))
              .map(getHalf)
              .map((gutterValue) => `${gutterValue * -1}`)
              .map(toPx),
          ),
        display: 'flex',
        flexDirection: direction,
        flexWrap: wrap ? 'wrap' : 'nowrap',
      }}
      {...props}
    >
      {childrenArray.map((child, key) =>
        React.isValidElement(child) ? React.cloneElement(child, { cols, key }) : child,
      )}
    </div>
  )
}

export const Col = ({ children, cols = 12, span, offset, ...props }) => {
  const { theme } = useThemeUI()
  if (Array.isArray(span) && !Array.isArray(cols)) {
    const colsNumber = cols
    cols = []
    for (let i = 0; i < span.length; i++) {
      cols.push(colsNumber)
    }
  }

  const columnValues = castArray(cols).map((colValue, index) => {
    const gutterValue = toPx(
      getValueFromBreakpointIndex(
        castArray(getGutterSize(theme)).map((value) => getValueFromSize(theme, value)),
        index,
      ),
    )
    const currentSpan = getValueFromBreakpointIndex(castArray(span), index)

    return { currentSpan, colValue, gutterValue }
  })

  const width = uncastArrayIfSingle(
    columnValues.map(({ currentSpan, colValue, gutterValue }) => {
      return `calc(${(currentSpan / colValue) * 100}% - ${gutterValue})`
    }),
  )

  return (
    <columnSizeContext.Provider value={columnValues}>
      <div
        sx={{
          boxSizing: 'border-box',
          mr: (theme) =>
            uncastArrayIfSingle(
              castArray(getGutterSize(theme))
                .map((value) => getValueFromSize(theme, value))
                .map(getHalf)
                .map(toPx),
            ),
          mb: getVerticalGutterSize,
          flexGrow: span ? 0 : 1,
          flexShrink: 0,
          flexBasis: `auto`,
          ...(span
            ? {
                width,
              }
            : {}),
          ...(offset
            ? {
                ml: (theme) =>
                  uncastArrayIfSingle(
                    castArray(cols).map((colValue, index) => {
                      const gutterValue = getValueFromBreakpointIndex(
                        castArray(getGutterSize(theme)).map((value) =>
                          getValueFromSize(theme, value),
                        ),
                        index,
                      )

                      const offsetValue = getValueFromBreakpointIndex(
                        castArray(offset).map((value) => getValueFromSize(theme, value)),
                        index,
                      )

                      return `calc(${(offsetValue / colValue) * 100}% + ${gutterValue / 2}px)`
                    }),
                  ),
              }
            : {
                ml: (theme) =>
                  uncastArrayIfSingle(
                    castArray(getGutterSize(theme))
                      .map((value) => getValueFromSize(theme, value))
                      .map(getHalf)
                      .map(toPx),
                  ),
              }),
        }}
        {...props}
      >
        {children}
      </div>
    </columnSizeContext.Provider>
  )
}

export const GridFromJson = ({ json, childrenArray }) => {
  const flatLayout = json.reduce((cells, { layout }) => cells.concat(layout), [])
  const usedKeys = flatLayout.map(({ key }) => key)

  let autoKey = 0
  // In the future, string key could be support for contentful block ID
  const getChildFromKey = (key) => {
    // If no key, search for the next key that isn't already used
    if (typeof key === 'undefined') {
      while (usedKeys.includes(autoKey)) {
        autoKey++
      }
      usedKeys.push(autoKey)
      key = autoKey
    }

    if (key === '*') {
      throw new Error('Use getWildcardChildren() for this')
    }

    // We need to pass an ID back as if the key is generated it'll be undefined
    // in the JSX
    return { id: key, component: childrenArray[key] }
  }

  const getWildcardChildren = () => {
    const wildcardCount = childrenArray.length - flatLayout.length + 1

    const wildCardChildren = []

    for (let i = 0; i < wildcardCount; i++) {
      wildCardChildren.push(getChildFromKey())
    }

    return wildCardChildren
  }

  return (
    <React.Fragment>
      {json.map(({ layout, ...rowProps }, i) => {
        return (
          <Row {...rowProps} key={i}>
            {layout.map(({ key, ...colProps }) => {
              if (key === '*') {
                return getWildcardChildren().map(({ id, component }) => (
                  <Col {...colProps} key={id} className='json-grid-col'>
                    {component}
                  </Col>
                ))
              }

              const { id, component } = getChildFromKey(key)
              return (
                <Col {...colProps} key={id} className='json-grid-col'>
                  {component}
                </Col>
              )
            })}
          </Row>
        )
      })}
    </React.Fragment>
  )
}
